/**
 * SubmitButton - Primary CTA button for payment form
 *
 * Features:
 * - Trial vs non-trial text variants
 * - Loading/submitting state with spinner
 * - Disabled state
 */

interface SubmitButtonProps {
  /** Whether this is a trial offer */
  isTrial?: boolean;
  /** Whether the form is submitting */
  isSubmitting?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Custom button text (overrides default) */
  children?: React.ReactNode;
}

export function SubmitButton({
  isTrial = true,
  isSubmitting = false,
  disabled = false,
  children,
}: SubmitButtonProps) {
  const defaultText = isTrial
    ? 'Start my risk assessment'
    : 'Complete payment';

  return (
    <button
      type="submit"
      disabled={disabled || isSubmitting}
      className="w-full rounded-full bg-catch-avocado-400 px-6 py-4 text-base font-semibold text-catch-dark-blue-90 transition-all hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-catch-avocado-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isSubmitting ? (
        <span className="inline-flex items-center gap-2">
          <svg
            className="h-5 w-5 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Processing...
        </span>
      ) : (
        children || defaultText
      )}
    </button>
  );
}

export default SubmitButton;
